import React, {
	useState,
	useEffect
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	Grid,
	TextField,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Link,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	OutlinedInput,
	Checkbox,
	ListItemText as MuiListItemText,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { AlertBox } from "../Alert/Alert";
import { LoadingStatusBox } from "../LoadingStatus/LoadingStatus";

function initialFieldValues(fields) {
	const values = {};
	Object.entries(fields || {}).forEach(([key, field]) => {
		if (field && typeof field === "object" && Object.prototype.hasOwnProperty.call(field, "value")) {
			values[key] = field.value;
		}
	});
	return values;
}

export default function ModalFull(props) {
	const [state, setState] = useState({
		modalBodyInputFields: initialFieldValues(props.modalBodyInputFields),
	});

	useEffect(() => {
		if (props.isModalOpen) {
			setState({
				modalBodyInputFields: initialFieldValues(props.modalBodyInputFields),
			});
		}
		// Intentionally only re-seed when the dialog opens; field defs are set in the same parent update.
	}, [props.isModalOpen]);

	const handleClose = () => {
		props.onModalClose(false);

		setState(prevState => ({
			...prevState,
			modalBodyInputFields: {}
		}));
	};

	const onChangeModalBodyInputField = (e) => {
		const { name, value } = e.target;

		setState(previousState => {
			return {
				...previousState,
				modalBodyInputFields: {
					...previousState.modalBodyInputFields,
					[name]: value,
				}
			};
		});
	};

	const renderInputField = (key, field) => {
		if (field.type === "multiselect") {
			const selected = state.modalBodyInputFields[key] ?? field.value ?? [];
			return (
				<FormControl key={key} fullWidth margin="normal" size="small">
					<InputLabel id={`${key}-label`}>{field.label}</InputLabel>
					<Select
						labelId={`${key}-label`}
						name={key}
						multiple
						value={Array.isArray(selected) ? selected : []}
						onChange={onChangeModalBodyInputField}
						input={<OutlinedInput label={field.label} />}
						renderValue={(selectedValues) =>
							(field.options || [])
								.filter((opt) => selectedValues.map(String).includes(String(opt.value)))
								.map((opt) => opt.label)
								.join(", ")
						}
					>
						{(field.options || []).map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								<Checkbox checked={(Array.isArray(selected) ? selected : []).map(String).includes(String(opt.value))} />
								<MuiListItemText primary={opt.label} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		if (field.type === "select") {
			const selected = state.modalBodyInputFields[key] ?? field.value ?? "";
			return (
				<FormControl key={key} fullWidth margin="normal" size="small">
					<InputLabel id={`${key}-label`}>{field.label}</InputLabel>
					<Select
						labelId={`${key}-label`}
						name={key}
						value={selected}
						label={field.label}
						onChange={onChangeModalBodyInputField}
					>
						{(field.options || []).map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		return (
			<TextField
				key={key}
				name={key}
				label={field.label}
				margin="normal"
				size="small"
				type={field.type}
				fullWidth
				defaultValue={field.value}
				onChange={onChangeModalBodyInputField}
			/>
		);
	};

	return (
		<Dialog
			open={props.isModalOpen}
			onClose={handleClose}
			keepMounted
			aria-describedby="alert-dialog-slide-description"
			fullWidth={true}
			maxWidth={props.modalSize ?? "sm"}
		>
			<DialogTitle>{props.modalBodyTitle}</DialogTitle>
			<DialogContent>

				{props.modalIsLoading && <LoadingStatusBox message="Loading..." />}

				{props.warningMessage && <AlertBox severity="warning" message={props.warningMessage} sx={{ mb:2 }}/>}

				{props.errorMessage && <AlertBox severity="error" message={props.errorMessage} sx={{ mb:2 }}/>}

				<DialogContentText id="alert-dialog-slide-description1">
					{props.modalBodyMessage1}
				</DialogContentText>

				{props.modalBodyMessage1Data && props.modalBodyMessage1Data}

				{props.modalBodyMessage1List && <List>
					{props.modalBodyMessage1List.map((s, i) =>
						<ListItem key={i}>
							<ListItemIcon>
								<CancelIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={s} />
						</ListItem>
					)
					}
				</List>
				}

				{props.modalBodyMessage2 && <DialogContentText id="alert-dialog-slide-description2" mt={2}>
					{props.modalBodyMessage2}
				</DialogContentText>
				}
				{props.modalBodyInputFields && (props.modalButtonActionType2 === "function") && (
					Object.entries(props.modalBodyInputFields).map(([key, value]) => renderInputField(key, value))
				)
				}
			</DialogContent>
			<DialogActions sx={{ display: "grid", justifyContent: "center", paddingBottom: "2rem" }}>
				<Grid
					container
					justifyContent="center"
				>
					<Grid item>
						{(props.modalButtonActionType1 === "close") && <Button
							variant={props.modalButtonVariant1}
							onClick={handleClose}
							color={props.modalButtonColor1}
						>
							{props.modalButtonText1}
						</Button>
						}
						{(props.modalButtonActionType1 === "function") && <Button
							variant={props.modalButtonVariant1}
							onClick={props.modalButtonAction1}
							color={props.modalButtonColor1}
						>
							{props.modalButtonText1}
						</Button>
						}
						{(props.modalButtonActionType1 === "link") && <Button
							variant="contained"
							component={RouterLink}
							rel="noopener noreferrer"
							to={props.modalButtonAction1}
							color={props.modalButtonColor1}
						>
							{props.modalButtonText1}
						</Button>
						}
						{(props.modalButtonActionType1 === "externalLink") && <Button
							variant="contained"
							component={Link}
							rel="noopener noreferrer"
							href={props.modalButtonAction1}
							target="_blank"
							color={props.modalButtonColor1}
						>
							{props.modalButtonText1}
						</Button>
						}
					</Grid>

					{(props.modalButtonActionType2 === "close" && props.modalButtonText2 !== "") && <Grid item ml={2}>
						<Button
							variant={props.modalButtonVariant2}
							onClick={handleClose}
							color={props.modalButtonColor2}
						>
							{props.modalButtonText2}
						</Button>
					</Grid>
					}
					{(props.modalButtonActionType2 === "function" && props.modalButtonText2 !== "") && <Grid item ml={2}>
						<Button
							variant={props.modalButtonVariant2}
							onClick={() => {
								props.modalButtonAction2(state.modalBodyInputFields);
							}}
							color={props.modalButtonColor2}
						>
							{props.modalButtonText2}
						</Button>
					</Grid>
					}
					{(props.modalButtonActionType2 === "link" && props.modalButtonText2 !== "") && <Grid item ml={2}>
						<Button
							variant="contained"
							color={props.modalButtonColor2}
							component={RouterLink}
							rel="noopener noreferrer"
							to={props.modalButtonAction2}
						>
							{props.modalButtonText2}
						</Button>
					</Grid>
					}
				</Grid>
			</DialogActions>
		</Dialog>
	);
}
